import React from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import { useEditProjectStatus } from "@planzone/view-models";
import { Identifiable, ProjectStatus } from "@planzone/dto";
import { SelectList } from "@common/selectList";
import { ColorPicker } from "@common/colorPicker";
import { FolderIcon } from "@common/folderIcon";
import { Props } from "./types";
import { RemoveIcon } from "./removeIcon";
import styles from "./styles.scss";

function isPersonalizedStatus({ label }: Identifiable<ProjectStatus>) {
  const defaultStatus = ["pz_closed", "pz_waiting", "pz_candidate", "pz_open"];

  return !defaultStatus.find((name) => name === label);
}

function Status({ status, remove }: Props) {
  const { t } = useTranslation();
  const { isRequestPending, editProjectStatus } = useEditProjectStatus(status);
  const isPersonalized = isPersonalizedStatus(status);
  const [name, setName] = React.useState(status.label);
  const [color, setColor] = React.useState(status.color);

  React.useEffect(() => {
    setName(status.label);
    setColor(status.color);
  }, [status.label, status.color]);

  return (
    <div
      className={classNames(styles.status, {
        [styles.personalized]: isPersonalized,
      })}
    >
      <div className={styles.wrapper}>
        {isPersonalized ? (
          <SelectList
            rootClassName={styles.colorPickerInput}
            listClassName={styles.colorPicker}
          >
            {() => ({
              input: (
                <FolderIcon className={styles.icon} style={{ fill: color }} />
              ),
              list: (
                <ColorPicker
                  color={color}
                  pick={setColor}
                  submit={() =>
                    !isRequestPending &&
                    editProjectStatus({
                      status,
                      nextStatus: { ...status, color },
                    })
                  }
                />
              ),
            })}
          </SelectList>
        ) : (
          <FolderIcon className={styles.icon} style={{ fill: color }} />
        )}
        {isPersonalized ? (
          <input
            type="text"
            className={classNames(styles.name, styles.custom)}
            value={name}
            disabled={isRequestPending}
            onChange={({ target }) => setName(target.value)}
            onBlur={() =>
              !isRequestPending &&
              editProjectStatus({
                status,
                nextStatus: {
                  ...status,
                  label: name,
                },
              })
            }
          />
        ) : (
          <p className={styles.name}>
            {t(`projectStatuses.${status.label}`, status.label || "Ouvert")}
          </p>
        )}
        {!isPersonalizedStatus(status) && (
          <p className={styles.description}>
            {t(`projectStatuses.description.${status.label}`, status.label)}
          </p>
        )}
      </div>
      {isPersonalizedStatus(status) && (
        <div className={styles.iconBox} onClick={() => remove(status)}>
          <RemoveIcon className={styles.icon} />
        </div>
      )}
    </div>
  );
}

export { Status };
